import type { ChangeRequest, ModerationEntity } from '../model/schema';
import { imagePatchesOf } from './change-summary';
import type { EntityBag } from './entity-bag';
import { formatBagValue } from './format';

export interface EntityDiffRow {
  key: string;
  live: string;
  pending: string;
  changed: boolean;
}

function diffComparable(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '[unserializable]';
  }
}

function isIdentityKey(key: string): boolean {
  return key === 'id' || key.endsWith('_id');
}

/**
 * Previous (`prod`) vs current (pending version entity) field comparison —
 * one row per key present on either side, `changed` when the normalized
 * values differ. `id`-ish identity keys sort last so content diffs read first.
 */
export function buildEntityDiff(
  live: ModerationEntity | null | undefined,
  pending: ModerationEntity | null | undefined,
): EntityDiffRow[] {
  const liveBag: EntityBag = live ?? {};
  const pendingBag: EntityBag = pending ?? {};
  const keys = [...new Set([...Object.keys(liveBag), ...Object.keys(pendingBag)])].sort(
    (a, b) => Number(isIdentityKey(a)) - Number(isIdentityKey(b)) || (a < b ? -1 : 1),
  );
  return keys.map((key) => {
    const liveValue: unknown = liveBag[key];
    const pendingValue: unknown = pendingBag[key];
    return {
      key,
      live: formatBagValue(liveValue),
      pending: formatBagValue(pendingValue),
      changed: diffComparable(liveValue) !== diffComparable(pendingValue),
    };
  });
}

export interface RequestDiffRow {
  key: string;
  /** Value before the request (`baseline`); `undefined` when unknown. */
  before: unknown;
  /** Value the request applies (`payload`). */
  after: unknown;
  /**
   * The merchant's own value when a reviewer amended the request and this
   * key differs from what applied (`original_payload`); else `undefined`.
   */
  original: unknown;
  amended: boolean;
}

/** Payload keys that are transport envelopes, not reviewable fields. */
const ENVELOPE_KEYS = new Set(['id', 'merchant_id', 'created_at', 'updated_at']);

/**
 * What a request changes, field by field: every top-level `payload` key
 * beside its `baseline` value (the entity as it stood when the request was
 * submitted) and — on a request amended before approval — the merchant's
 * original value from `original_payload`. Keys only the original carries
 * (the reviewer dropped them) are listed too.
 */
export function buildRequestDiff(
  request: Pick<ChangeRequest, 'payload' | 'baseline' | 'original_payload'>,
): RequestDiffRow[] {
  const payload = structOf(request.payload) ?? {};
  const baseline = structOf(request.baseline);
  const original = structOf(request.original_payload);
  const keys = [
    ...new Set([...Object.keys(payload), ...Object.keys(original ?? {})]),
  ].filter((key) => !ENVELOPE_KEYS.has(key));
  return keys.map((key) => {
    const after: unknown = payload[key];
    const originalValue: unknown = original?.[key];
    const amended =
      original !== null && diffComparable(originalValue) !== diffComparable(after);
    return {
      key,
      before: baseline?.[key],
      after,
      original: amended ? originalValue : undefined,
      amended,
    };
  });
}

/**
 * Normalizes a payload / baseline value so the same data in the two wire
 * shapes compares equal: unset ≈ `null` ≈ `''` ≈ `false`, numeric strings ≈
 * numbers, the PATCH `{ids: [...]}` wrapper ≈ a plain id array, and object
 * key order does not matter.
 */
function normalizeForCompare(value: unknown): unknown {
  if (value === null || value === undefined || value === '' || value === false) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const asNumber = Number(trimmed);
    return trimmed !== '' && Number.isFinite(asNumber) ? asNumber : trimmed;
  }
  if (Array.isArray(value)) return value.map(normalizeForCompare);
  if (typeof value === 'object') {
    const bag = value as Record<string, unknown>;
    const keys = Object.keys(bag);
    if (keys.length === 1 && keys[0] === 'ids' && Array.isArray(bag.ids)) {
      return normalizeForCompare(bag.ids);
    }
    return Object.fromEntries(
      keys.sort().map((key) => [key, normalizeForCompare(bag[key])]),
    );
  }
  return value;
}

function sameValue(a: unknown, b: unknown): boolean {
  return (
    JSON.stringify(normalizeForCompare(a) ?? null) ===
    JSON.stringify(normalizeForCompare(b) ?? null)
  );
}

/**
 * Keys the request's `payload` really changes against its own `baseline`
 * (same wire shape on both sides), sorted.
 */
function payloadChangedKeys(
  request: Pick<ChangeRequest, 'payload' | 'baseline'> | null | undefined,
): string[] {
  const payload = structOf(request?.payload);
  if (!payload) return [];
  const baseline = structOf(request?.baseline);
  return Object.keys(payload)
    .filter((key) => !ENVELOPE_KEYS.has(key) && !sameValue(baseline?.[key], payload[key]))
    .sort();
}

/**
 * The entity key a flat payload key is rendered under: the payload speaks the
 * storage names (`address_line`, `category_ids`) while the entity readers and
 * sections use the structured ones (`address`, `restaurant_category_ids`).
 */
const PAYLOAD_KEY_ALIASES: Record<string, string> = {
  address_line: 'address',
  latitude: 'address',
  longitude: 'address',
  category_ids: 'restaurant_category_ids',
};

/**
 * The fields a request really changes. The server's `changed_fields` is
 * authoritative whenever it is sent; when it is empty (the list is only
 * filled against a `prod` version) the request's own `payload` is compared
 * with its own `baseline` — both are the same wire shape, so an untouched
 * field (coordinates, address line, categories, `clear_*` flags…) never
 * reads as changed the way a live-row-vs-proposed-row diff made it.
 */
export function requestChangedKeys(
  request: Pick<ChangeRequest, 'payload' | 'baseline'> | null | undefined,
  serverKeys: readonly string[] = [],
): string[] {
  const patchKeys = request ? imagePatchChangedKeys(request) : [];
  if (serverKeys.length > 0) return [...new Set([...serverKeys, ...patchKeys])].sort();
  const keys = payloadChangedKeys(request);
  const aliases = keys.flatMap((key) => PAYLOAD_KEY_ALIASES[key] ?? []);
  return [...new Set([...keys, ...aliases, ...patchKeys])].sort();
}

/**
 * A picture patch is an operation, so the server's `changed_fields` may not
 * name the slot it touches — a removed branch picture then had no row to
 * review. The patch itself says what changed: `image_patches` and the entity
 * field behind each slot it names.
 */
function imagePatchChangedKeys(request: Pick<ChangeRequest, 'payload'>): string[] {
  const patches = imagePatchesOf(request);
  if (patches.length === 0) return [];
  const fields = patches.flatMap(
    (patch) => IMAGE_SLOT_FIELDS[(patch.slot || patch.key).toLowerCase()] ?? [],
  );
  return ['image_patches', ...fields];
}

/**
 * The pending entity with the request's real changes laid over it. The card's
 * entity is the live row with the patch "already applied", but when the
 * server did not apply a field (empty `changed_fields`, an unmapped flat key)
 * it still carries the OLD value — so the moderator saw the previous address
 * or minimum order after the merchant changed it. The request's own payload is
 * the source of truth for what was asked, so each changed key is written over
 * the entity, flat storage keys translated to the structured shape.
 */
export function applyRequestChanges(
  entity: ModerationEntity,
  request: Pick<ChangeRequest, 'payload' | 'baseline'> | null | undefined,
): ModerationEntity {
  const payload = structOf(request?.payload);
  const keys = payloadChangedKeys(request);
  if (!payload || keys.length === 0) return entity;

  const next: Record<string, unknown> = { ...entity };
  const address: Record<string, unknown> = { ...(structOf(next.address) ?? {}) };
  let addressTouched = false;
  for (const key of keys) {
    const value = payload[key];
    switch (key) {
      case 'address_line':
        address.line = typeof value === 'string' ? { ru: value } : value;
        addressTouched = true;
        break;
      case 'latitude':
      case 'longitude':
        address[key] = typeof value === 'string' ? Number(value) : value;
        addressTouched = true;
        break;
      case 'category_ids':
        next.restaurant_category_ids = value;
        next[key] = value;
        break;
      default:
        next[key] = value;
    }
  }
  if (addressTouched) next.address = address;
  applyImagePatches(next, { payload });
  return next;
}

/** Entity field behind each restaurant picture slot an image patch names. */
const IMAGE_SLOT_FIELDS: Record<string, string> = {
  icon: 'icon',
  logo: 'icon',
  background: 'background_image',
  background_image: 'background_image',
};
const IMAGE_REMOVAL_OPS = new Set(['delete', 'remove', 'detach', 'unlink']);

/**
 * Lays `payload.image_patches` over the entity's picture fields. The patch is
 * an operation, not a value, so without this a deleted icon / background kept
 * the old picture on the "after" side and the reviewer never saw it go: a
 * removal blanks the field (the review then draws its «no photo» tile), an
 * upload with a `link` swaps in the new picture.
 */
function applyImagePatches(
  next: Record<string, unknown>,
  request: Pick<ChangeRequest, 'payload'>,
): void {
  for (const patch of imagePatchesOf(request)) {
    const field = IMAGE_SLOT_FIELDS[(patch.slot || patch.key).toLowerCase()];
    if (!field) continue;
    if (IMAGE_REMOVAL_OPS.has(patch.op.toLowerCase())) next[field] = '';
    else if (patch.link !== '') next[field] = patch.link;
  }
}

function structOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
